/* eslint-disable @typescript-eslint/no-explicit-any */

export type EditorRewriteResult = {
	editorId: string;
	previous: string;
	next: string;
};

/**
 * Directly mutates the `<span data-placeholder="column_<id>_…|…">…</span>`
 * elements inside every mounted TinyMCE editor so the visible chip immediately
 * reflects the new snippet name.
 *
 * This is a low-level companion to `rewriteAllTinyMceEditors` — it runs at the
 * DOM level so it works even if the `setContent` / `BeforeSetContent` round
 * trip for some reason fails to refresh the visible chips.
 */
export const updateColumnPlaceholdersInTinyMce = (columnId: number, newName: string): number => {
	if (typeof window === "undefined") {
		return 0;
	}

	const tinymce = (window as any).tinymce;

	if (!tinymce?.editors) {
		return 0;
	}

	let updated = 0;
	const newPlaceholder = `column_${columnId}_${newName}|${newName}`;
	const selector = `span[data-placeholder^="column_${columnId}_"]`;

	for (const editor of tinymce.editors as any[]) {
		try {
			const body = editor.getBody?.();

			if (!body || typeof body.querySelectorAll !== "function") {
				continue;
			}

			const spans = body.querySelectorAll(selector) as NodeListOf<HTMLElement>;

			spans.forEach((span) => {
				span.setAttribute("data-placeholder", newPlaceholder);
				span.setAttribute("title", "");
				span.textContent = newName;
				updated += 1;
			});
		} catch {
			// Best-effort.
		}
	}

	return updated;
};

/**
 * Applies a content transformation to every currently-mounted TinyMCE
 * editor and returns the before/after content for each editor whose
 * content actually changed.
 *
 * This is used after renaming an AI snippet so that:
 *   1. the editor's *in-memory* content is updated (so the user immediately
 *      sees the new chip label), and
 *   2. the caller can use the *post-rewrite* content (rather than a closure-
 *      captured stale `selectedNode.data.message`) when persisting parent
 *      state — eliminating any "older state saved after newer" races.
 *
 * TinyMCE reads `initialValue` only once on mount, so a fresh value coming
 * through React props would otherwise be ignored, and the next blur would
 * write the editor's stale content back over our rewrite.
 */
export const rewriteAllTinyMceEditors = (
	transform: (html: string) => string
): EditorRewriteResult[] => {
	if (typeof window === "undefined") {
		return [];
	}

	const tinymce = (window as any).tinymce;

	if (!tinymce?.editors) {
		return [];
	}

	const results: EditorRewriteResult[] = [];

	for (const editor of tinymce.editors as any[]) {
		try {
			if (typeof editor.getContent !== "function" || typeof editor.setContent !== "function") {
				continue;
			}

			const previous: string = editor.getContent();
			const next = transform(previous);

			if (next === previous) {
				continue;
			}

			editor.setContent(next);
			editor.setDirty?.(true);
			editor.fire?.("change");

			results.push({
				editorId: editor.id ?? "",
				previous,
				next,
			});
		} catch {
			// Best-effort — ignore individual editor failures.
		}
	}

	return results;
};
